import { f7, Page, Popup } from 'framework7-react';
import { FC, useEffect, useCallback, useRef } from 'react';
import { AIChat } from './AIChat/AIChat';
import { paths } from '@/config/paths';
import { useAIChat } from '@/hooks/api/ai/useAIChat';
import { useAISession } from '@/hooks/api/ai/useAISession';

/** AIが提示する問い合わせ導線の選択肢。プロンプト側の文言と一致させる */
const INQUIRY_CHOICE_LABEL = 'お問い合わせする';

const innerStyle = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column' as const,
  backgroundColor: '#FFFFFF',
};

const centeredStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column' as const,
  justifyContent: 'center',
  alignItems: 'center',
  padding: '20px',
};

export const AIChatPage: FC = () => {
  const {
    session,
    isLoading: isSessionLoading,
    error: sessionError,
    fetchSession,
  } = useAISession();
  const {
    messages,
    isLoading,
    isSending,
    isStreaming,
    streamingContent,
    error,
    hasMoreHistory,
    isLoadingOlder,
    sendMessage,
    loadOlderMessages,
    connectWebSocket,
  } = useAIChat(session);

  // セッション取得は表示時の1回だけ。
  // fetchSession が新しい session を返すと connectWebSocket の参照も変わるため、
  // 依存配列に任せると取得→再実行の無限ループになる
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) {
      return;
    }

    startedRef.current = true;

    fetchSession().then((s) => {
      if (s) {
        // WebSocket接続を試みる（未設定ならHTTPにフォールバックする）
        connectWebSocket();
      }
    });
  }, [fetchSession, connectWebSocket]);

  // チャットを閉じる（ポップアップとして開いているため popup.close を使う）
  const handleClose = useCallback(() => {
    f7.popup.close();
  }, []);

  // メッセージ送信
  const handleSendMessage = useCallback(
    (message: string) => {
      // AIが提示する「お問い合わせする」はチャットに投げるのではなく
      // アプリのお問い合わせ画面へ遷移させる。
      // 担当者の回答をチャットに戻す仕組み（管理画面側）が未実装のため、
      // 現時点の窓口はアプリのお問い合わせになる。
      if (message === INQUIRY_CHOICE_LABEL) {
        f7.popup.close();
        f7.view.main.router.navigate(paths.contact);
        return;
      }

      sendMessage(message);
    },
    [sendMessage],
  );

  const renderContent = () => {
    // セッション取得中
    if (isSessionLoading) {
      return (
        <div style={centeredStyle}>
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                margin: '0 auto 16px',
                border: '3px solid #E0E0E0',
                borderTopColor: '#1E55E6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
            <div style={{ color: '#666' }}>読み込み中...</div>
            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      );
    }

    // セッション取得エラー
    if (sessionError) {
      return (
        <div style={centeredStyle}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>?</div>
          <div
            style={{
              fontSize: '16px',
              color: '#C62828',
              textAlign: 'center',
              marginBottom: '24px',
            }}
          >
            {sessionError}
          </div>
          <button
            onClick={handleClose}
            style={{
              padding: '12px 32px',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#FFFFFF',
              backgroundColor: '#1E55E6',
              border: 'none',
              borderRadius: '24px',
              cursor: 'pointer',
            }}
          >
            閉じる
          </button>
        </div>
      );
    }

    return (
      <AIChat
        messages={messages}
        isLoading={isLoading}
        isSending={isSending}
        isStreaming={isStreaming}
        streamingContent={streamingContent}
        error={error}
        hasMoreHistory={hasMoreHistory}
        isLoadingOlder={isLoadingOlder}
        onSendMessage={handleSendMessage}
        onLoadOlder={loadOlderMessages}
        onClose={handleClose}
      />
    );
  };

  // ログインの車両選択と同じ Popup を使う。
  // 背景のスクロール止めは Framework7 が html.with-modal-popup で行うため自前では持たない
  return (
    <Popup closeByBackdropClick swipeToClose>
      <Page name="AIChat" pageContent={false}>
        <div style={innerStyle}>{renderContent()}</div>
      </Page>
    </Popup>
  );
};
